import type { AdminExhibitionProduct } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { STATUS_LABELS } from '@/entities/product';
import { Button, EmptyState, StatusBadge } from '@/shared/ui';

interface ExhibitionLinkedProductListProps {
  readonly products: readonly AdminExhibitionProduct[];
  readonly isPending: boolean;
  readonly isBusy: boolean;
  readonly onRemove: (productId: string) => void;
}

/** 기획전에 묶인 상품 목록 — 숨김·삭제된 상품도 상태와 함께 남겨 운영자가 알아보게 한다. */
export const ExhibitionLinkedProductList = ({
  products,
  isPending,
  isBusy,
  onRemove,
}: ExhibitionLinkedProductListProps): ReactElement => (
  <div className="mt-4 border-t border-foreground/[0.06] pt-4">
    <p className="text-sm font-medium">묶인 상품 {products.length}개</p>

    {isPending ? (
      <div className="mt-2">
        <EmptyState>불러오는 중…</EmptyState>
      </div>
    ) : products.length === 0 ? (
      <div className="mt-2">
        <EmptyState>묶인 상품이 없습니다.</EmptyState>
      </div>
    ) : (
      <ul className="mt-2 flex flex-col gap-2">
        {products.map((product) => (
          <li key={product.productId} className="flex items-center gap-3">
            {product.thumbnail !== null ? (
              <img
                src={product.thumbnail.url}
                alt=""
                width={32}
                height={32}
                className="size-8 rounded-lg object-cover"
              />
            ) : (
              <span aria-hidden className="size-8 rounded-lg bg-muted" />
            )}
            <span className="min-w-0 flex-1 truncate text-sm">{product.name}</span>
            {product.isDeleted ? (
              <StatusBadge tone="danger">삭제됨</StatusBadge>
            ) : (
              <StatusBadge tone={STATUS_LABELS[product.status].tone}>
                {STATUS_LABELS[product.status].label}
              </StatusBadge>
            )}
            <Button
              size="sm"
              variant="ghost"
              disabled={isBusy}
              onClick={() => onRemove(product.productId)}
              aria-label={`${product.name} 제외`}
            >
              제외
            </Button>
          </li>
        ))}
      </ul>
    )}
  </div>
);
